import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { describeImportError } from '../utils/importError';
import Pagination from '../components/Pagination';
import SearchableSelect from '../components/SearchableSelect';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const ROLE_LABELS: Record<string, string> = {
  owner: 'Эзэмшигч', renter: 'Түрээслэгч', family: 'Гэр бүл',
};

export default function ResidentsPage() {
  const { user } = useAuth();
  const isPlatform = user?.role === 'platform_admin';
  const [residents, setResidents] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [buildingFilter, setBuildingFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [tenantOptions, setTenantOptions] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [units, setUnits] = useState<any[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; errors: any[] } | null>(null);
  const [importError, setImportError] = useState('');
  const importRef = useRef<HTMLInputElement>(null);

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API}/admin/residents/import-template`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      if (!res.ok) throw new Error('Загвар татахад алдаа гарлаа');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'oршин_суугч_загвар.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.message || 'Алдаа гарлаа');
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    setImportError('');

    if (file.size > 10 * 1024 * 1024) {
      setImportError('Файл хэт том байна (10MB-аас дээш). Цөөн мөрөөр хуваан оруулна уу.');
      setImporting(false);
      if (importRef.current) importRef.current.value = '';
      return;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 120000);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/admin/residents/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        body: fd,
        signal: controller.signal,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Сервер алдаа буцаалаа (HTTP ${res.status})`);
      }
      const data = await res.json();
      setImportResult({ imported: data.imported || 0, errors: data.errors || [] });
      load();
    } catch (err: any) {
      setImportError(describeImportError(err));
    } finally {
      window.clearTimeout(timeoutId);
      setImporting(false);
      if (importRef.current) importRef.current.value = '';
    }
  };
  const [form, setForm] = useState({
    phone: '', first_name: '', last_name: '', email: '', unit_id: '', role: 'owner',
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ phone: '', first_name: '', last_name: '', email: '', is_active: true });
  const [addUnitFor, setAddUnitFor] = useState<any | null>(null);
  const [addUnitForm, setAddUnitForm] = useState({ unit_id: '', role: 'owner' });
  const [addFamilyFor, setAddFamilyFor] = useState<any | null>(null);
  const [addFamilyForm, setAddFamilyForm] = useState({ phone: '', first_name: '', last_name: '', email: '', unit_id: '', role: 'family' });
  const [addFamilySaving, setAddFamilySaving] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const load = (q?: string, pageArg?: number) => {
    setLoading(true);
    const params = new URLSearchParams();
    const term = q !== undefined ? q : search;
    const p = pageArg !== undefined ? pageArg : page;
    if (term) params.set('search', term);
    if (roleFilter) params.set('role', roleFilter);
    if (buildingFilter) params.set('building', buildingFilter);
    if (activeFilter) params.set('is_active', activeFilter);
    if (tenantFilter) params.set('tenant_id', tenantFilter);
    params.set('sort_by', sortBy);
    params.set('sort_dir', sortDir);
    params.set('skip', String(p * PAGE_SIZE));
    params.set('limit', String(PAGE_SIZE));
    const qs = params.toString();
    api.get(`/admin/residents${qs ? `?${qs}` : ''}`)
      .then(res => { setResidents(res?.items || []); setTotal(res?.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const loadUnits = () => {
    if (units.length === 0) {
      api.get('/admin/units?limit=1000').then(res => setUnits(res?.items || [])).catch(() => {});
    }
  };

  useEffect(() => { loadUnits(); }, []);
  useEffect(() => {
    if (isPlatform) api.get('/admin/users/tenants').then(res => setTenantOptions(res?.items || [])).catch(() => {});
  }, [isPlatform]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(0); load(undefined, 0); }, [roleFilter, buildingFilter, activeFilter, tenantFilter]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [page]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(0); load(undefined, 0); }, [sortBy, sortDir]);

  const toggleSort = (col: string) => {
    if (sortBy === col) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('asc'); }
  };

  const SortTh = ({ col, children }: { col: string; children: React.ReactNode }) => (
    <th style={{ ...th, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => toggleSort(col)}
        title="Эрэмбэлэх">
      {children}{' '}
      <span style={{ color: sortBy === col ? '#236844' : '#c8cfc9', fontSize: 11 }}>
        {sortBy === col ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </th>
  );

  const handleSearch = () => { setPage(0); load(search, 0); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/admin/residents', {
        ...form,
        unit_id: form.unit_id || undefined,
      });
      setShowForm(false);
      setForm({ phone: '', first_name: '', last_name: '', email: '', unit_id: '', role: 'owner' });
      load(search);
    } catch (e: any) {
      alert(e?.body || 'Алдаа гарлаа');
    }
  };

  const startEdit = (r: any) => {
    setEditId(r.id);
    setEditForm({ phone: r.phone, first_name: r.first_name, last_name: r.last_name, email: r.email || '', is_active: r.is_active });
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.put(`/admin/residents/${editId}`, editForm);
      setEditId(null);
      load(search);
    } catch (e: any) {
      alert(e?.body || 'Алдаа гарлаа');
    }
  };

  const handleResend = async (id: string, email: string) => {
    if (!window.confirm(`${email} руу нэвтрэх мэдээлэл дахин илгээх үү?`)) return;
    try {
      await api.post(`/admin/residents/${id}/resend-credentials`, {});
      alert('Нэвтрэх мэдээлэл илгээлээ');
    } catch (e: any) {
      alert(e?.body ? JSON.parse(e.body)?.detail || 'Алдаа' : 'Алдаа');
    }
  };

  const startAddUnit = (r: any) => {
    setAddUnitFor(r);
    setAddUnitForm({ unit_id: '', role: 'owner' });
    loadUnits();
  };

  const handleAddUnit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addUnitFor || !addUnitForm.unit_id) return;
    try {
      await api.post('/admin/residents', {
        phone: addUnitFor.phone,
        unit_id: addUnitForm.unit_id,
        role: addUnitForm.role,
      });
      setAddUnitFor(null);
      load(search);
    } catch (e: any) {
      alert(e?.body || 'Алдаа гарлаа');
    }
  };

  const startAddFamily = (r: any) => {
    setAddFamilyFor(r);
    // Хэрэв тухайн оршин суугч нэг байртай бол автоматаар сонгоно.
    const units = r.units || [];
    setAddFamilyForm({
      phone: '', first_name: '', last_name: '', email: '',
      unit_id: units.length === 1 ? units[0].unit_id : '',
      role: 'family',
    });
    loadUnits();
  };

  const handleAddFamily = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addFamilyForm.phone.trim() || !addFamilyForm.unit_id) {
      alert('Утас болон орон сууц заавал'); return;
    }
    setAddFamilySaving(true);
    try {
      await api.post('/admin/residents', {
        phone: addFamilyForm.phone.trim(),
        first_name: addFamilyForm.first_name.trim(),
        last_name: addFamilyForm.last_name.trim(),
        email: addFamilyForm.email.trim() || undefined,
        unit_id: addFamilyForm.unit_id,
        role: addFamilyForm.role,
      });
      setAddFamilyFor(null);
      load(search);
    } catch (e: any) {
      alert(e?.body ? (JSON.parse(e.body)?.detail || 'Алдаа') : 'Алдаа гарлаа');
    } finally {
      setAddFamilySaving(false);
    }
  };

  const handleRemoveUnit = async (residentId: string, unitId: string) => {
    if (!window.confirm('Орон сууцнаас хасах уу?')) return;
    try {
      await api.delete(`/admin/residents/${residentId}/units/${unitId}`);
      load(search);
    } catch (e: any) {
      alert(e?.body || 'Алдаа гарлаа');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontFamily: "'Inter'", fontWeight: 700, color: '#1c2b22' }}>
          Оршин суугчид <span style={{ fontWeight: 400, fontSize: 16, color: '#5f6f66' }}>({total})</span>
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={downloadTemplate} style={btnGhost} title="Excel загвар татаж авах">↓ Загвар татах</button>
          <button onClick={() => importRef.current?.click()} disabled={importing} style={{ ...btnOutline, opacity: importing ? 0.6 : 1 }}>
            {importing ? 'Импортлож байна...' : '⇪ Excel импорт'}
          </button>
          <input ref={importRef} type="file" accept=".xlsx,.xls" onChange={handleImport} style={{ display: 'none' }} />
          <button onClick={() => setShowForm(!showForm)} style={btnPrimary}>
            {showForm ? 'Болих' : '+ Оршин суугч нэмэх'}
          </button>
        </div>
      </div>

      {importError && (
        <div style={{
          background: '#fff', borderRadius: 12, padding: 16, marginBottom: 16,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)', borderLeft: '4px solid #dc2626',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div>
              <strong style={{ color: '#dc2626' }}>⚠️ Импорт амжилтгүй боллоо</strong>
              <div style={{ marginTop: 6, fontSize: 13.5, color: '#7a2020', lineHeight: 1.55 }}>{importError}</div>
            </div>
            <button onClick={() => setImportError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5f6f66', fontSize: 18 }}>✕</button>
          </div>
        </div>
      )}

      {importResult && (
        <div style={{
          background: '#fff', borderRadius: 12, padding: 16, marginBottom: 16,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          borderLeft: `4px solid ${importResult.errors.length ? '#f59e0b' : '#236844'}`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong style={{ color: '#1c2b22' }}>
              ✅ {importResult.imported} оршин суугч импортлогдлоо
              {importResult.errors.length > 0 && ` · ⚠️ ${importResult.errors.length} мөр алгасагдсан`}
            </strong>
            <button onClick={() => setImportResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5f6f66', fontSize: 18 }}>✕</button>
          </div>
          {importResult.errors.length > 0 && (
            <div style={{ marginTop: 10, maxHeight: 160, overflowY: 'auto', fontSize: 13, color: '#92620e' }}>
              {importResult.errors.slice(0, 30).map((er: any, i: number) => (
                <div key={i}>• {er.row}-р мөр: {er.error}</div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          placeholder="Нэр, утас хайх..."
          style={{ ...inputStyle, flex: 1, minWidth: 200, maxWidth: 280 }}
        />
        <button onClick={handleSearch} style={{ ...btnPrimary, padding: '8px 16px' }}>Хайх</button>
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={{ ...inputStyle, maxWidth: 150 }}>
          <option value="">Бүх үүрэг</option>
          <option value="owner">Эзэмшигч</option>
          <option value="renter">Түрээслэгч</option>
          <option value="family">Гэр бүл</option>
        </select>
        <select value={buildingFilter} onChange={e => setBuildingFilter(e.target.value)} style={{ ...inputStyle, maxWidth: 150 }}>
          <option value="">Бүх байр</option>
          {Array.from(new Set(units.map((u: any) => u.building).filter(Boolean))).sort().map((b: any) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
        <select value={activeFilter} onChange={e => setActiveFilter(e.target.value)} style={{ ...inputStyle, maxWidth: 140 }}>
          <option value="">Бүх төлөв</option>
          <option value="true">Идэвхтэй</option>
          <option value="false">Идэвхгүй</option>
        </select>
        {isPlatform && (
          <select value={tenantFilter} onChange={e => setTenantFilter(e.target.value)} style={{ ...inputStyle, maxWidth: 200 }}>
            <option value="">Бүх СӨХ</option>
            {tenantOptions.map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
        {(roleFilter || buildingFilter || activeFilter || tenantFilter || search) && (
          <button onClick={() => { setSearch(''); setRoleFilter(''); setBuildingFilter(''); setActiveFilter(''); setTenantFilter(''); load(''); }}
            style={{ background: 'none', border: 'none', color: '#236844', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Цэвэрлэх</button>
        )}
      </div>

      {showForm && (
        <div style={card}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>Утас *</label>
                <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} style={inputStyle} required placeholder="99001122" />
              </div>
              <div>
                <label style={labelStyle}>Овог</label>
                <input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Нэр</label>
                <input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>И-мэйл</label>
                <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle} type="email" />
              </div>
              <div>
                <label style={labelStyle}>Орон сууц</label>
                <SearchableSelect
                  options={units.map((u: any) => ({ value: u.id, label: `${u.building} - ${u.unit_number}` }))}
                  value={form.unit_id}
                  onChange={v => setForm({ ...form, unit_id: v })}
                  placeholder="Хайх: блок эсвэл тоот"
                />
              </div>
              <div>
                <label style={labelStyle}>Хэрэглэгчийн төрөл</label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} style={inputStyle}>
                  <option value="owner">Эзэмшигч</option>
                  <option value="renter">Түрээслэгч</option>
                  <option value="family">Гэр бүл</option>
                </select>
              </div>
            </div>
            <button type="submit" style={btnPrimary}>Нэмэх</button>
          </form>
        </div>
      )}

      {loading ? <p>Уншиж байна...</p> : (
        <div style={card}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e8e5dc', textAlign: 'left' }}>
                <th style={{ ...th, width: 48 }}>№</th>
                <SortTh col="name">Нэр</SortTh>
                {isPlatform && <th style={th}>СӨХ</th>}
                <SortTh col="phone">Утас</SortTh>
                <SortTh col="email">И-мэйл</SortTh>
                <SortTh col="unit">Орон сууц</SortTh>
                <th style={th}>Төлөв</th>
                <th style={th}>Үйлдэл</th>
              </tr>
            </thead>
            <tbody>
              {residents.map((r: any, i: number) => (
                <React.Fragment key={r.id}>
                <tr style={{ borderBottom: '1px solid #e8e5dc' }}>
                  <td style={{ ...td, color: '#8a978f' }}>{page * PAGE_SIZE + i + 1}</td>
                  <td style={td}><strong>{r.last_name} {r.first_name}</strong></td>
                  {isPlatform && <td style={td}>{(r.tenant_names && r.tenant_names.length) ? r.tenant_names.join(', ') : '-'}</td>}
                  <td style={td}>{r.phone}</td>
                  <td style={td}>{r.email || '-'}</td>
                  <td style={td}>
                    {r.units?.map((u: any) => (
                      <span key={u.unit_id} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        background: '#f0fdf4', padding: '2px 8px', borderRadius: 4, fontSize: 12, marginRight: 4,
                      }}>
                        {u.building}-{u.unit_number}
                        <span style={{ color: '#5f6f66', fontSize: 10 }}>({ROLE_LABELS[u.role] || u.role})</span>
                        <button
                          onClick={() => handleRemoveUnit(r.id, u.unit_id)}
                          style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12, padding: 0 }}
                          title="Хасах"
                        >&times;</button>
                      </span>
                    ))}
                    {(!r.units || r.units.length === 0) && <span style={{ color: '#94a3b8' }}>-</span>}
                  </td>
                  <td style={td}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                      background: r.is_active ? '#dcfce7' : '#fee2e2',
                      color: r.is_active ? '#166534' : '#dc2626',
                    }}>
                      {r.is_active ? 'Идэвхтэй' : 'Идэвхгүй'}
                    </span>
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => startAddFamily(r)} style={{ ...btnSmall, color: '#236844', fontWeight: 700 }} title="Гэр бүлийн гишүүн нэмэх">＋</button>
                      <button onClick={() => startEdit(r)} style={btnSmall} title="Засах">{'✎'}</button>
                      <button onClick={() => startAddUnit(r)} style={btnSmall} title="Орон сууц нэмэх">{'+⌂'}</button>
                      {r.email && <button onClick={() => handleResend(r.id, r.email)} style={btnSmall} title="Нэвтрэх мэдээлэл дахин илгээх">{'✉'}</button>}
                    </div>
                  </td>
                </tr>
                {editId === r.id && (
                  <tr style={{ background: '#fafaf5' }}>
                    <td colSpan={isPlatform ? 8 : 7} style={{ padding: 12 }}>
                      <form onSubmit={handleEditSubmit} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div>
                          <label style={labelStyle}>Утас</label>
                          <input value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} style={{ ...inputStyle, width: 140 }} />
                        </div>
                        <div>
                          <label style={labelStyle}>Овог</label>
                          <input value={editForm.last_name} onChange={e => setEditForm({ ...editForm, last_name: e.target.value })} style={{ ...inputStyle, width: 140 }} />
                        </div>
                        <div>
                          <label style={labelStyle}>Нэр</label>
                          <input value={editForm.first_name} onChange={e => setEditForm({ ...editForm, first_name: e.target.value })} style={{ ...inputStyle, width: 140 }} />
                        </div>
                        <div>
                          <label style={labelStyle}>И-мэйл</label>
                          <input value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} style={{ ...inputStyle, width: 180 }} type="email" />
                        </div>
                        <div>
                          <label style={labelStyle}>Төлөв</label>
                          <select value={editForm.is_active ? 'true' : 'false'} onChange={e => setEditForm({ ...editForm, is_active: e.target.value === 'true' })} style={{ ...inputStyle, width: 120 }}>
                            <option value="true">Идэвхтэй</option>
                            <option value="false">Идэвхгүй</option>
                          </select>
                        </div>
                        <button type="submit" style={{ ...btnPrimary, padding: '8px 16px' }}>Хадгалах</button>
                        <button type="button" onClick={() => setEditId(null)} style={{ ...btnSmall, padding: '8px 12px' }}>Болих</button>
                      </form>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
              {residents.length === 0 && (
                <tr><td colSpan={isPlatform ? 8 : 7} style={{ ...td, textAlign: 'center', color: '#94a3b8' }}>Оршин суугч бүртгэгдээгүй</td></tr>
              )}
            </tbody>
          </table>

          <Pagination page={page} pageSize={PAGE_SIZE} total={total} count={residents.length} onChange={setPage} />
        </div>
      )}

      {addUnitFor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setAddUnitFor(null)}>
          <div style={{ ...card, width: 400, maxWidth: '90%' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontFamily: "'Inter'", color: '#1c2b22' }}>
              Орон сууц нэмэх — {addUnitFor.last_name} {addUnitFor.first_name}
            </h3>
            <form onSubmit={handleAddUnit}>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Орон сууц *</label>
                <SearchableSelect
                  options={units.map((u: any) => ({ value: u.id, label: `${u.building} - ${u.unit_number}` }))}
                  value={addUnitForm.unit_id}
                  onChange={v => setAddUnitForm({ ...addUnitForm, unit_id: v })}
                  placeholder="Хайх: блок эсвэл тоот"
                  allowClear={false}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Төрөл</label>
                <select value={addUnitForm.role} onChange={e => setAddUnitForm({ ...addUnitForm, role: e.target.value })} style={inputStyle}>
                  <option value="owner">Эзэмшигч</option>
                  <option value="renter">Түрээслэгч</option>
                  <option value="family">Гэр бүл</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" style={btnPrimary}>Нэмэх</button>
                <button type="button" onClick={() => setAddUnitFor(null)} style={{ ...btnSmall, padding: '8px 16px' }}>Болих</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Гэр бүлийн гишүүн нэмэх */}
      {addFamilyFor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setAddFamilyFor(null)}>
          <div style={{ ...card, width: 460, maxWidth: '92%' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 4px', fontFamily: "'Inter'", color: '#1c2b22' }}>Гэр бүлийн гишүүн нэмэх</h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#5f6f66' }}>
              {addFamilyFor.last_name} {addFamilyFor.first_name}-ийн орон сууцанд шинэ гишүүн бүртгэнэ
            </p>
            <form onSubmit={handleAddFamily} style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Овог</label>
                  <input value={addFamilyForm.last_name} onChange={e => setAddFamilyForm({ ...addFamilyForm, last_name: e.target.value })} style={inputStyle} placeholder="Дорж" />
                </div>
                <div>
                  <label style={labelStyle}>Нэр</label>
                  <input value={addFamilyForm.first_name} onChange={e => setAddFamilyForm({ ...addFamilyForm, first_name: e.target.value })} style={inputStyle} placeholder="Бат" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Утас *</label>
                  <input value={addFamilyForm.phone} onChange={e => setAddFamilyForm({ ...addFamilyForm, phone: e.target.value })} style={inputStyle} placeholder="99001122" required />
                </div>
                <div>
                  <label style={labelStyle}>И-мэйл</label>
                  <input value={addFamilyForm.email} onChange={e => setAddFamilyForm({ ...addFamilyForm, email: e.target.value })} style={inputStyle} type="email" placeholder="bat@mail.mn" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Орон сууц *</label>
                  <SearchableSelect
                    options={(addFamilyFor.units && addFamilyFor.units.length > 0
                      ? addFamilyFor.units.map((u: any) => ({ value: u.unit_id, label: `${u.building} - ${u.unit_number}` }))
                      : units.map((u: any) => ({ value: u.id, label: `${u.building} - ${u.unit_number}` })))}
                    value={addFamilyForm.unit_id}
                    onChange={v => setAddFamilyForm({ ...addFamilyForm, unit_id: v })}
                    placeholder="Хайх: блок эсвэл тоот"
                    allowClear={false}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Төрөл</label>
                  <select value={addFamilyForm.role} onChange={e => setAddFamilyForm({ ...addFamilyForm, role: e.target.value })} style={inputStyle}>
                    <option value="family">Гэр бүл</option>
                    <option value="owner">Эзэмшигч</option>
                    <option value="renter">Түрээслэгч</option>
                  </select>
                </div>
              </div>
              <p style={{ margin: 0, fontSize: 12, color: '#8a9a90' }}>И-мэйл оруулбал нэвтрэх мэдээлэл автоматаар илгээгдэнэ.</p>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button type="submit" disabled={addFamilySaving} style={{ ...btnPrimary, opacity: addFamilySaving ? 0.7 : 1 }}>{addFamilySaving ? 'Нэмж байна...' : 'Нэмэх'}</button>
                <button type="button" onClick={() => setAddFamilyFor(null)} style={{ ...btnSmall, padding: '8px 16px' }}>Болих</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const btnPrimary: React.CSSProperties = {
  background: 'linear-gradient(180deg, #2f7d52, #236844)', color: '#fff', border: 'none', padding: '8px 20px',
  borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14, fontFamily: "'Inter'",
};
const btnOutline: React.CSSProperties = {
  background: 'transparent', color: '#236844', border: '1.5px solid #236844', padding: '8px 18px',
  borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14, fontFamily: "'Inter'",
};
const btnGhost: React.CSSProperties = {
  background: '#f0ede3', color: '#2a3b32', border: 'none', padding: '8px 18px',
  borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14, fontFamily: "'Inter'",
};
const card: React.CSSProperties = {
  background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};
const labelStyle: React.CSSProperties = { display: 'block', marginBottom: 4, fontSize: 13, color: '#2a3b32', fontWeight: 500 };
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', border: '1.5px solid #dcdacf', borderRadius: 10, fontSize: 14, boxSizing: 'border-box', background: '#fbfaf6',
};
const btnSmall: React.CSSProperties = {
  background: '#f0fdf4', border: '1px solid #d1d5db', padding: '4px 10px',
  borderRadius: 8, cursor: 'pointer', fontSize: 13, color: '#2a3b32', fontFamily: "'Inter'",
};
const th: React.CSSProperties = { padding: '10px 12px', fontSize: 13, color: '#5f6f66', fontWeight: 600 };
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 14, color: '#1c2b22' };
